import React from 'react';
import { Box } from '@mui/material';
import { formatNumber } from '../../utils/customFormat';
import { CustomerHealthCheck_T } from '../../types/Customer';
import SummaryCard from '../UI/SummaryCard';

interface HealthCheckSummaryCardsProps {
  healthChecks: CustomerHealthCheck_T[];
  loading?: boolean;
  onCardClick?: (filterType: string) => void;
  selectedFilter?: string;
}

const HealthCheckSummaryCards: React.FC<HealthCheckSummaryCardsProps> = ({
  healthChecks,
  loading = false,
  onCardClick,
  selectedFilter = 'all'
}) => {
  // Get pre-calculated unique counts from the backend API
  // These counts represent unique customers with issues, not summed individual check counts
  const criticalCount = healthChecks.length > 0 ? healthChecks[0].total_customers_with_critical_issues : 0;
  const urgentCount = healthChecks.length > 0 ? healthChecks[0].total_customers_with_urgent_issues : 0;

  if (loading || !healthChecks?.length) {
    return (
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4].map((i) => (
          <SummaryCard
            key={i}
            title="Loading..."
            value="--"
            metricType="info"
          />
        ))}
      </Box>
    );
  }

  // Get the total unique customers with issues from the health checks data
  const customersWithIssuesCount = healthChecks.length > 0 ? healthChecks[0].total_customers_with_issues : 0;
  const customerHasIssues = customersWithIssuesCount > 0;

  // Calculate healthy customers metrics (focusing on Current customers only)
  const totalCurrentCustomers = healthChecks.length > 0 ? healthChecks[0].total_current_customers : 0;
  const currentCustomersWithIssuesCount = healthChecks.length > 0 ? healthChecks[0].total_current_customers_with_issues : 0;
  const healthyCurrentCustomersCount = totalCurrentCustomers - currentCustomersWithIssuesCount;
  const healthyCurrentPercentage = totalCurrentCustomers > 0 ? (healthyCurrentCustomersCount / totalCurrentCustomers) * 100 : 0;

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box 
          onClick={() => onCardClick?.('all')}
          sx={{ 
            opacity: selectedFilter === 'all' ? 1 : (customerHasIssues ? 0.8 : 0.7),
            transform: selectedFilter === 'all' ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title="Customer with issues"
            value={formatNumber(customersWithIssuesCount || 0)}
            subtitle={customerHasIssues ? 'Customers requiring attention' : 'No issues'}
            metricType="issues"
            clickable={customerHasIssues}
            isActive={selectedFilter === 'all'}
          />
        </Box>
        <Box 
          sx={{ 
            cursor: 'default',
            opacity: 0.9,
            transform: 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title="Healthy Current Customers"
            value={`${healthyCurrentPercentage.toFixed(1)}%`}
            subtitle={`${formatNumber(healthyCurrentCustomersCount)} of ${formatNumber(totalCurrentCustomers)} current customers`}
            metricType="success"
          />
        </Box>

        <Box 
          onClick={() => onCardClick?.('critical')}
          sx={{ 
            opacity: selectedFilter === 'critical' ? 1 : (criticalCount > 0 ? 0.8 : 0.7),
            transform: selectedFilter === 'critical' ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title="Missing Critical Data"
            value={formatNumber(criticalCount)}
            subtitle="Data gaps in customer set up fields"
            metricType="requirement"
            clickable={criticalCount > 0}
            isActive={selectedFilter === 'critical'}
          />
        </Box>

        <Box 
          onClick={() => onCardClick?.('urgent')}
          sx={{ 
            opacity: selectedFilter === 'urgent' ? 1 : (urgentCount > 0 ? 0.8 : 0.7),
            transform: selectedFilter === 'urgent' ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title="Urgent Data Issues"
            value={formatNumber(urgentCount)}
            subtitle="Wrong/Missing Date related fields"
            metricType="date_related"
            clickable={urgentCount > 0}
            isActive={selectedFilter === 'urgent'}
          />
        </Box>
      </Box>
    </>
  );
};

export default HealthCheckSummaryCards;
